import React, { useContext, useEffect, useState } from "react";
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Checkbox,
  Box,
  FormControlLabel,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "../../../contexts/InfoContext";
import { useAuth } from "../../../contexts/AuthContext";
import fetchData from "../../../api/services/Users/getUser";
import getInternationalDate from "../../../utils/getDate";
import { SpinnerContext } from "../../../contexts/SpinnerContext";
import CustomTextField from "../../VersatileComponents/orderTextInput";
import getRequiredUserData from "../../../utils/getBranchInfo";
import create from "../../../api/orders/create";
import useUserClaims from "../../../hooks/useUserClaims";
import capitalizeString from "../../../utils/capitalizeString";
import { getOrderTypes } from "../../../api/services/orderTypes/orderTypes";
import checkCustomerMembership from "../../../api/customer/checkCustomer";
import checkCustomerBlock from "../../../api/customer/checkCustomerBlock";

// Validation schema
const AsbezaOrderFormValidationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  blockHouse: Yup.string().required("Block House is required"),
  phone: Yup.string().required("Phone is required"),
  deliveryguyId: Yup.string().required("Please select a Delivery Guy"),
  branchId: Yup.string().required("Please select a Branch"),
  customerType: Yup.string().required("Customer Type is required"),
  price: Yup.number().when("customerType", {
    is: (value) => value === "negotiable",
    then: () =>
      Yup.number()
        .required("Price is required for negotiable orders")
        .positive("Price must be positive"),
    otherwise: () => Yup.number().nullable(),
  }),
  order: Yup.array()
    .of(
      Yup.object().shape({
        isCustom: Yup.boolean().required(),
        category: Yup.string().when("isCustom", {
          is: false,
          then: () => Yup.string().required("Category is required"),
          otherwise: () => Yup.string().nullable(),
        }),
        type: Yup.string().required("Order type is required"),
        measurement: Yup.string().required("Measurement is required"),
        quantity: Yup.number()
          .required("Quantity is required")
          .positive("Quantity must be positive"),
        additionalInfo: Yup.string().nullable(), // Optional field
      })
    )
    .min(1, "At least one order item is required"),
});

const DEBOUNCE_DELAY = 1000; // milliseconds

const AsbezaOrderBranchForm = () => {
  const [showForm, setShowForm] = useState(false);
  const { openSnackbar } = useSnackbar();
  const { user } = useAuth();
  const theme = useTheme();
  const { setIsSubmitting } = useContext(SpinnerContext);
  const [deliveryGuy, setDeliveryGuy] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customerType, setCustomerType] = useState("regular");
  const [isMembership, setIsMembership] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false); // State for block status
  const userData = getRequiredUserData();
  const userClaims = useUserClaims(user);

  // Fetch delivery guys
  useEffect(() => {
    const unsubscribe = fetchData("Deliveryturn", setDeliveryGuy);
    return () => unsubscribe();
  }, []);

  // Fetch categories and order types
  useEffect(() => {
    getOrderTypes()
      .then((categoriesData) => setCategories(categoriesData || []))
      .catch((error) => {
        console.error("Error fetching categories:", error);
        openSnackbar("Failed to fetch categories.", "error");
      });
  }, []);

  // Initialize formik
  const formik = useFormik({
    initialValues: {
      name: "",
      phone: "",
      blockHouse: "",
      additionalInfo: "",
      order: [],
      deliveryguyName: "",
      branchName: userData.branchName || "",
      branchId: userData.requiredId || "",
      deliveryguyId: "",
      activeTable: userData.activeTable || "",
      active: userData.active || "",
      activeDailySummery: userData.activeDailySummery || "",
      customerType: "regular",
      price: "",
      deliveryType: "regular",
    },
    validationSchema: AsbezaOrderFormValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const date = getInternationalDate();
        const name = capitalizeString(values.name);

        if (!values.deliveryguyId || !values.deliveryguyName) {
          handleCloseForm();
          const error = new Error(
            "Delivery guy information is not found. Please check your connection, refresh your browser, and try again."
          );
          error.response = {
            data: { message: error.message, type: "error" },
          };
          throw error;
        }

        if (
          !values.branchId ||
          !values.branchName ||
          !values.activeTable ||
          !values.active ||
          !values.activeDailySummery
        ) {
          handleCloseForm();
          const error = new Error(
            "Branch information is not found. Please check your connection, refresh your browser, and try again."
          );
          error.response = {
            data: { message: error.message, type: "error" },
          };
          throw error;
        }

        values.name = name;
        values.date = date;
        values.callcenterId = userData.requiredId || user.displayName;
        values.customerType = customerType;
        values.status = "Assigned";
        values.branchKey = values.callcenterId;
        values.blockHouse = values.blockHouse.toUpperCase();
        values.from = "branch";
        values.order = values.order.map((item) => ({
          ...item,
          quantity: parseFloat(item.quantity),
          // Ensure category is null for custom orders
          category: item.isCustom ? "custom" : item.category,
        }));

        if (values.customerType === "negotiable" && values.price) {
          values.price = parseFloat(values.price);
        } else {
          delete values.price;
        }
        values.deliveryType = "regular";
        const res = await create(user, values, "asbeza");
        openSnackbar(`${res.data.message}!`, "success");
        handleCloseForm();
      } catch (error) {
        if (error.response && error.response.data) {
          openSnackbar(
            error.response.data.message,
            error.response.data.type ? error.response.data.type : "error"
          );
        } else {
          openSnackbar(
            "An unexpected error occurred. Please check your connection.",
            "error"
          );
        }
      }
      setIsSubmitting(false);
    },
  });

  // Check customer membership and block status
  useEffect(() => {
    const handler = setTimeout(() => {
      const checkCustomer = async () => {
        if (!formik.values.blockHouse) {
          setCustomerType("regular");
          setIsMembership(false);
          setIsBlocked(false);
          formik.setFieldValue("customerType", "regular");
          return;
        }

        try {
          // Check block status
          const isBlocked = await checkCustomerBlock(formik.values.blockHouse);
          setIsBlocked(isBlocked);
          if (isBlocked) {
            openSnackbar(
              "This customer is blocked and cannot place orders.",
              "error"
            );
            return;
          }

          // Check membership status
          const isMember = await checkCustomerMembership(
            formik.values.blockHouse
          );
          if (isMember) {
            setCustomerType("membership");
            setIsMembership(true);
            formik.setFieldValue("customerType", "membership");
          } else {
            setCustomerType("regular");
            setIsMembership(false);
            formik.setFieldValue("customerType", "regular");
          }
        } catch (error) {
          console.error("Error checking customer:", error);
          openSnackbar("Failed to check customer status.", "error");
          setCustomerType("regular");
          setIsMembership(false);
          setIsBlocked(false);
          formik.setFieldValue("customerType", "regular");
        }
      };

      checkCustomer();
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(handler);
  }, [formik.values.blockHouse]);

  const handleDeliveryGuyChange = (event) => {
    const Id = event.target.value;
    const Name = deliveryman.find((employee) => employee[1] === Id)?.[0] || "";
    formik.setFieldValue("deliveryguyName", Name);
    formik.setFieldValue("deliveryguyId", Id);
  };

  const handleNegotiableChange = (event) => {
    const isChecked = event.target.checked;
    if (!isMembership) {
      const newType = isChecked ? "negotiable" : "regular";
      setCustomerType(newType);
      formik.setFieldValue("customerType", newType);
      if (!isChecked) {
        formik.setFieldValue("price", "");
      }
    }
  };

  const handleButtonClick = () => {
    if (userData.activeTable) {
      setShowForm(true);
    } else {
      openSnackbar(
        `${userData.branchName} branch does not have a daily table today or sheet. So you can't create an order. Please create a sheet or table.`,
        "info"
      );
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setIsBlocked(false); // Reset block status
    formik.resetForm();
  };

  const handleAddOrderItem = () => {
    formik.setFieldValue("order", [
      ...formik.values.order,
      {
        isCustom: false,
        category: "",
        type: "",
        measurement: "",
        quantity: "",
        additionalInfo: "",
      },
    ]);
  };

  const handleRemoveOrderItem = (index) => {
    const newOrder = [...formik.values.order];
    newOrder.splice(index, 1);
    formik.setFieldValue("order", newOrder);
  };

  const handleOrderItemChange = (index, field, value) => {
    const newOrder = [...formik.values.order];
    newOrder[index] = { ...newOrder[index], [field]: value };

    if (field === "isCustom") {
      // Reset fields when toggling custom mode
      newOrder[index].category = "";
      newOrder[index].type = "";
      newOrder[index].measurement = "";
      newOrder[index].additionalInfo = "";
    } else if (field === "category" && !newOrder[index].isCustom) {
      newOrder[index].type = "";
      newOrder[index].measurement = "";
      newOrder[index].additionalInfo = "";
    } else if (field === "type" && !newOrder[index].isCustom) {
      // Automatically set the measurement for predefined orders
      const selectedCategory = categories.find(
        (cat) => cat.name === newOrder[index].category
      );
      const selectedType = selectedCategory?.orderTypes.find(
        (t) => t.name === value
      );
      newOrder[index].measurement = selectedType?.measurement || "";
      newOrder[index].additionalInfo = "";
    }

    formik.setFieldValue("order", newOrder);
  };

  const deliveryMan = deliveryGuy
    ? deliveryGuy[formik.values.branchId ? formik.values.branchId : ""]
    : [];
  const deliveryman = deliveryMan?.map((item) => [
    item.deliveryGuyName,
    item.deliveryManId,
  ]);

  useEffect(() => {
    const { branchId, activeTable, branchName } = formik.values;
    if (branchId && !activeTable) {
      openSnackbar(
        `${branchName} branch does not have a daily table today or sheet. So you can't create an order. Please create Sheet or Table.`,
        "info"
      );
    }
  }, [formik.values]);

  return (
    <div>
      {userClaims.admin ? (
        <Button variant="contained" color="primary" onClick={handleButtonClick}>
          Create New Asbeza Order
        </Button>
      ) : null}

      <Dialog open={showForm} onClose={handleCloseForm} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: theme.palette.background.alt }}>
          New Asbeza Order
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: theme.palette.background.alt }}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              {/* Name */}
              <CustomTextField
                name="name"
                label="Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.name && formik.touched.name}
                helperText={
                  formik.errors.name && formik.touched.name
                    ? formik.errors.name
                    : ""
                }
                touched={formik.touched.name}
              />

              {/* Phone */}
              <CustomTextField
                name="phone"
                label="Phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.phone && formik.touched.phone}
                helperText={
                  formik.errors.phone && formik.touched.phone
                    ? formik.errors.phone
                    : ""
                }
                touched={formik.touched.phone}
              />

              {/* Block/House */}
              <CustomTextField
                name="blockHouse"
                label="Block/House"
                value={formik.values.blockHouse}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.blockHouse && formik.touched.blockHouse}
                helperText={
                  formik.errors.blockHouse && formik.touched.blockHouse
                    ? formik.errors.blockHouse
                    : ""
                }
                touched={formik.touched.blockHouse}
              />

              {/* Customer Type and Block Status Display */}
              <Grid item xs={12}>
                <Typography variant="subtitle1">
                  Customer Type:{" "}
                  {customerType.charAt(0).toUpperCase() + customerType.slice(1)}
                </Typography>
                {isBlocked && (
                  <Typography variant="subtitle1" color="error">
                    Customer is blocked and cannot place orders.
                  </Typography>
                )}
              </Grid>

              {/* Negotiable Checkbox */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customerType === "negotiable"}
                      onChange={handleNegotiableChange}
                      disabled={isMembership || isBlocked} // Disable if blocked
                      color="primary"
                    />
                  }
                  label="Negotiable Order"
                />
              </Grid>

              {/* Price (for Negotiable) */}
              {customerType === "negotiable" && (
                <CustomTextField
                  name="price"
                  label="Custom Price"
                  type="number"
                  value={formik.values.price}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.errors.price && formik.touched.price}
                  helperText={
                    formik.errors.price && formik.touched.price
                      ? formik.errors.price
                      : ""
                  }
                  touched={formik.touched.price}
                />
              )}

              {/* Delivery Guy */}
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormControl
                  fullWidth
                  error={
                    formik.touched.deliveryguyId &&
                    Boolean(formik.errors.deliveryguyId)
                  }
                >
                  <InputLabel
                    shrink
                    style={{
                      marginTop: "8px",
                      color: theme.palette.secondary[100],
                    }}
                  >
                    Delivery Guy
                  </InputLabel>
                  <Select
                    name="deliveryguyId"
                    value={formik.values.deliveryguyId}
                    onChange={handleDeliveryGuyChange}
                    onBlur={formik.handleBlur}
                    label="Delivery Guy"
                    disabled={isBlocked} // Disable if blocked
                  >
                    <MenuItem value="">Select Delivery Guy</MenuItem>
                    {deliveryman?.map((branch) => (
                      <MenuItem key={branch[1]} value={branch[1]}>
                        {branch[0]}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.deliveryguyId &&
                    formik.errors.deliveryguyId && (
                      <Typography
                        variant="caption"
                        color="error"
                        style={{ marginTop: "4px" }}
                      >
                        {formik.errors.deliveryguyId}
                      </Typography>
                    )}
                </FormControl>
              </Grid>

              {/* Additional Info (for entire order) */}
              <CustomTextField
                name="additionalInfo"
                label="Additional Info"
                value={formik.values.additionalInfo}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.errors.additionalInfo && formik.touched.additionalInfo
                }
                helperText={
                  formik.errors.additionalInfo && formik.touched.additionalInfo
                    ? formik.errors.additionalInfo
                    : ""
                }
                touched={formik.touched.additionalInfo}
                required={false}
              />

              {/* Order Items */}
              {formik.values.order.map((item, index) => (
                <Grid item xs={12} key={index}>
                  <Grid container spacing={1} alignItems="center">
                    {/* Custom Order Checkbox */}
                    <Grid item xs={12} sm={1} md={1} lg={1}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <Typography
                          variant="caption"
                          color={theme.palette.secondary[100]}
                          sx={{ mb: 0.5 }}
                        >
                          Custom
                        </Typography>
                        <Checkbox
                          checked={item.isCustom}
                          onChange={(e) =>
                            handleOrderItemChange(
                              index,
                              "isCustom",
                              e.target.checked
                            )
                          }
                          disabled={isBlocked} // Disable if blocked
                          color="primary"
                          size="small"
                        />
                      </Box>
                    </Grid>

                    {/* Category (only for predefined orders) */}
                    {!item.isCustom && (
                      <Grid item xs={12} sm={2} md={2} lg={2}>
                        <FormControl
                          fullWidth
                          error={
                            formik.touched.order?.[index]?.category &&
                            Boolean(formik.errors.order?.[index]?.category)
                          }
                          disabled={isBlocked} // Disable if blocked
                        >
                          <InputLabel
                            shrink
                            style={{
                              marginTop: "8px",
                              color: theme.palette.secondary[100],
                            }}
                          >
                            Category {index + 1}
                          </InputLabel>
                          <Select
                            name={`order[${index}].category`}
                            value={item.category}
                            onChange={(e) =>
                              handleOrderItemChange(
                                index,
                                "category",
                                e.target.value
                              )
                            }
                            onBlur={formik.handleBlur}
                            label={`Category ${index + 1}`}
                          >
                            <MenuItem value="">Select Category</MenuItem>
                            {categories.map((category) => (
                              <MenuItem
                                key={category.name}
                                value={category.name}
                              >
                                {category.name.charAt(0).toUpperCase() +
                                  category.name.slice(1)}
                              </MenuItem>
                            ))}
                          </Select>
                          {formik.touched.order?.[index]?.category &&
                            formik.errors.order?.[index]?.category && (
                              <Typography
                                variant="caption"
                                color="error"
                                style={{ marginTop: "4px" }}
                              >
                                {formik.errors.order[index].category}
                              </Typography>
                            )}
                        </FormControl>
                      </Grid>
                    )}

                    {/* Order Type */}
                    <Grid item xs={12} sm={2} md={2} lg={2}>
                      {item.isCustom ? (
                        <TextField
                          name={`order[${index}].type`}
                          label="Custom Order Type"
                          value={item.type}
                          onChange={(e) =>
                            handleOrderItemChange(index, "type", e.target.value)
                          }
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.order?.[index]?.type &&
                            Boolean(formik.errors.order?.[index]?.type)
                          }
                          helperText={
                            formik.touched.order?.[index]?.type &&
                            formik.errors.order?.[index]?.type
                              ? formik.errors.order[index].type
                              : ""
                          }
                          fullWidth
                          disabled={isBlocked} // Disable if blocked
                          InputLabelProps={{
                            shrink: true,
                            style: { color: theme.palette.secondary[100] },
                          }}
                        />
                      ) : (
                        <FormControl
                          fullWidth
                          error={
                            formik.touched.order?.[index]?.type &&
                            Boolean(formik.errors.order?.[index]?.type)
                          }
                          disabled={!item.category || isBlocked} // Disable if blocked
                        >
                          <InputLabel
                            shrink
                            style={{
                              marginTop: "8px",
                              color: theme.palette.secondary[100],
                            }}
                          >
                            Order Type {index + 1}
                          </InputLabel>
                          <Select
                            name={`order[${index}].type`}
                            value={item.type}
                            onChange={(e) =>
                              handleOrderItemChange(
                                index,
                                "type",
                                e.target.value
                              )
                            }
                            onBlur={formik.handleBlur}
                            label={`Order Type ${index + 1}`}
                          >
                            <MenuItem value="">Select Type</MenuItem>
                            {categories
                              .find((cat) => cat.name === item.category)
                              ?.orderTypes.map((type) => (
                                <MenuItem key={type.name} value={type.name}>
                                  {type.name.charAt(0).toUpperCase() +
                                    type.name.slice(1)}
                                </MenuItem>
                              ))}
                          </Select>
                          {formik.touched.order?.[index]?.type &&
                            formik.errors.order?.[index]?.type && (
                              <Typography
                                variant="caption"
                                color="error"
                                style={{ marginTop: "4px" }}
                              >
                                {formik.errors.order[index].type}
                              </Typography>
                            )}
                        </FormControl>
                      )}
                    </Grid>

                    {/* Measurement */}
                    <Grid item xs={12} sm={2} md={2} lg={2}>
                      {item.isCustom ? (
                        <TextField
                          name={`order[${index}].measurement`}
                          label="Custom Measurement"
                          value={item.measurement}
                          onChange={(e) =>
                            handleOrderItemChange(
                              index,
                              "measurement",
                              e.target.value
                            )
                          }
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.order?.[index]?.measurement &&
                            Boolean(formik.errors.order?.[index]?.measurement)
                          }
                          helperText={
                            formik.touched.order?.[index]?.measurement &&
                            formik.errors.order?.[index]?.measurement
                              ? formik.errors.order[index].measurement
                              : ""
                          }
                          fullWidth
                          disabled={isBlocked} // Disable if blocked
                          InputLabelProps={{
                            shrink: true,
                            style: { color: theme.palette.secondary[100] },
                          }}
                        />
                      ) : (
                        <Typography
                          variant="body1"
                          style={{
                            marginTop: "8px",
                            color: item.type
                              ? theme.palette.secondary[100]
                              : theme.palette.text.disabled,
                          }}
                        >
                          Measurement:{" "}
                          {item.type
                            ? categories
                                .find((cat) => cat.name === item.category)
                                ?.orderTypes.find((t) => t.name === item.type)
                                ?.measurement.charAt(0)
                                .toUpperCase() +
                                categories
                                  .find((cat) => cat.name === item.category)
                                  ?.orderTypes.find((t) => t.name === item.type)
                                  ?.measurement.slice(1) || "N/A"
                            : "Select an order type"}
                        </Typography>
                      )}
                      {formik.touched.order?.[index]?.measurement &&
                        formik.errors.order?.[index]?.measurement && (
                          <Typography
                            variant="caption"
                            color="error"
                            style={{ marginTop: "4px" }}
                          >
                            {formik.errors.order[index].measurement}
                          </Typography>
                        )}
                    </Grid>

                    {/* Quantity */}
                    <Grid item xs={12} sm={2} md={2} lg={2}>
                      <TextField
                        name={`order[${index}].quantity`}
                        label="Quantity"
                        type="number"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) =>
                          handleOrderItemChange(
                            index,
                            "quantity",
                            e.target.value
                          )
                        }
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.order?.[index]?.quantity &&
                          Boolean(formik.errors.order?.[index]?.quantity)
                        }
                        helperText={
                          formik.touched.order?.[index]?.quantity &&
                          formik.errors.order?.[index]?.quantity
                            ? formik.errors.order[index].quantity
                            : ""
                        }
                        fullWidth
                        disabled={isBlocked} // Disable if blocked
                        InputLabelProps={{
                          shrink: true,
                          style: { color: theme.palette.secondary[100] },
                        }}
                      />
                    </Grid>

                    {/* Additional Info (for order item) */}
                    <Grid item xs={12} sm={2} md={2} lg={2}>
                      <TextField
                        name={`order[${index}].additionalInfo`}
                        label="Additional Info"
                        value={item.additionalInfo || ""}
                        onChange={(e) =>
                          handleOrderItemChange(
                            index,
                            "additionalInfo",
                            e.target.value
                          )
                        }
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.order?.[index]?.additionalInfo &&
                          Boolean(formik.errors.order?.[index]?.additionalInfo)
                        }
                        helperText={
                          formik.touched.order?.[index]?.additionalInfo &&
                          formik.errors.order?.[index]?.additionalInfo
                            ? formik.errors.order[index].additionalInfo
                            : ""
                        }
                        fullWidth
                        disabled={isBlocked} // Disable if blocked
                        InputLabelProps={{
                          shrink: true,
                          style: { color: theme.palette.secondary[100] },
                        }}
                      />
                    </Grid>

                    {/* Remove Button */}
                    <Grid item xs={12} sm={1} md={1} lg={1}>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleRemoveOrderItem(index)}
                        sx={{ height: "100%" }}
                        disabled={isBlocked} // Disable if blocked
                      >
                        Remove
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              ))}

              {/* Add Order Button */}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleAddOrderItem}
                  disabled={isBlocked} // Disable if blocked
                >
                  Add Order Item
                </Button>
              </Grid>

              {/* Form Actions */}
              <Grid
                item
                xs={12}
                container
                alignItems="end"
                justifyContent="end"
              >
                <DialogActions
                  sx={{ backgroundColor: theme.palette.background.alt }}
                >
                  <Button
                    variant="contained"
                    onClick={handleCloseForm}
                    color="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className="submit-button"
                    disabled={isBlocked || formik.isSubmitting} // Disable if blocked
                  >
                    Submit
                  </Button>
                </DialogActions>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AsbezaOrderBranchForm;
